import { useNavigate } from 'react-router-dom';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import {
  getPathWithRef,
  getUrlParams,
  getUrlSearchParamsObj,
  isKeyInObject,
  splitAmountRounded,
} from 'helpers/utils';
import {
  clearDonationSelections,
  getDonationSelections,
  setDonationSelections,
} from 'services/storage';
import { ProcessingRedirectOptions } from 'store/donatePage/constants';
import { useAppSelector } from 'helpers/hooks';
import { useEffect, useState } from 'react';
import { getDonatePageData } from 'selectors/donatePage';
import { getProcessingRedirect } from 'helpers/donate';
import { getEventData } from 'selectors/eventPage';
import { IPage } from 'store/donatePage/types';
import { IEvent } from 'store/eventPage/types';

export const useOathCheckout = () => {
  return (
    candidates: CandidateType[],
    amount: number,
    selectedTags?: string[],
    page?: string,
    issue?: string
  ) => {
    const splits = splitAmountRounded(amount, candidates.length);
    const useCustomSplits = isKeyInObject('donationAmount', candidates[0]);
    const donationSelections = {
      candidates: candidates.map((candidate, index) => ({
        ...candidate,
        donationAmount: useCustomSplits ? candidate.donationAmount : splits[index],
      })),
      total: amount.toString(),
    };

    // remove sessionStorage item if it was previously set and not removed
    clearDonationSelections();

    // convert donationSelection object to string so it can be stored in sessionStorage
    const donationSelectionsString = JSON.stringify(donationSelections);
    // save donationSelections to sessionStorage after user hits donate button on partner/issue/main page
    // and BEFORE user enters login/signup flow
    setDonationSelections(donationSelectionsString);

    const searchParamObj = getUrlSearchParamsObj();

    if (page) {
      searchParamObj.set('p', page);
    }
    if (issue) {
      searchParamObj.set('issue', issue);
    }
    searchParamObj.delete('tags');

    let queryStr = searchParamObj.toString() || '';
    let navPath = '/checkout';

    if (selectedTags) {
      const tagsQueryStr = selectedTags.map(tag => `tags=${tag.replaceAll(' ', '%20')}`).join('&');

      queryStr += queryStr ? `&${tagsQueryStr}` : tagsQueryStr;
    }

    if (queryStr) {
      navPath += `?${queryStr}`;
    }

    window.location.href = getPathWithRef(navPath);
  };
};

export const useRedirectToCheckout = () => {
  const navigate = useNavigate();

  return () => {
    const donationSelections = getDonationSelections();
    if (!donationSelections) {
      return false;
    }
    const donationSelectionObj = JSON.parse(donationSelections);
    navigate(
      getPathWithRef(`${donationSelectionObj.pathname}?${donationSelectionObj.queryParams}`)
    );
    return true;
  };
};

export const useIsOathCheckoutEnabled = () => {
  const { urlPartner } = getUrlParams();
  const [isEnabled, setIsEnabled] = useState(false);
  const [pageData, setPageData] = useState<IPage | IEvent>();
  const { donatePageData, eventData } = useAppSelector(state => ({
    donatePageData: getDonatePageData(state),
    eventData: getEventData(state),
  }));

  useEffect(() => {
    if (donatePageData) setPageData(donatePageData);
    else if (eventData) setPageData(eventData);
  }, [donatePageData, eventData]);

  useEffect(() => {
    if (pageData || !urlPartner) {
      setIsEnabled(
        !urlPartner || getProcessingRedirect(pageData) === ProcessingRedirectOptions.DE_OATH
      );
    }
  }, [pageData]);

  return isEnabled;
};

export const useDisclaimerData = () => {
  const { urlPartner, urlEvent } = getUrlParams();
  const [customDisclaimer, setCustomDisclaimer] = useState<string | undefined>(undefined);
  const [isOathDisclaimerVisible, setIsOathDisclaimerVisible] = useState<boolean>(true);
  const { donatePageData, eventData } = useAppSelector(state => ({
    donatePageData: getDonatePageData(state),
    eventData: getEventData(state),
  }));

  useEffect(() => {
    if (urlPartner && donatePageData && !urlEvent) {
      setCustomDisclaimer(donatePageData.custom_disclaimer);
      setIsOathDisclaimerVisible(donatePageData.is_oath_disclaimer_visible);
    } else if (urlPartner && urlEvent && eventData) {
      setCustomDisclaimer(eventData.custom_disclaimer);
      setIsOathDisclaimerVisible(eventData.is_oath_disclaimer_visible);
    }
  }, [urlPartner, urlEvent, donatePageData, eventData]);

  return { customDisclaimer, isOathDisclaimerVisible };
};
